// 학생 순공부시간 페이지

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Divider,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useStudentAuth } from '../hooks/useStudentAuth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface StudyTimeData {
  today_minutes: number;
  week_minutes: number;
  month_minutes: number;
  rank?: number;
  total_students?: number;
}

export default function StudyTimePage() {
  const { student } = useStudentAuth();
  const [data, setData] = useState<StudyTimeData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchStudyTime();
  }, [student]);

  const fetchStudyTime = async () => {
    if (!student?.id) return;

    try {
      setIsLoading(true);
      const token = localStorage.getItem('student_access_token');

      const response = await fetch(`${API_BASE_URL}/api/v1/study-time/student/${student.id}/summary`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('순공부시간 조회 에러:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}시간 ${mins}분`;
  };

  const todayGoal = 240; // 4시간 목표
  const todayProgress = data ? Math.min((data.today_minutes / todayGoal) * 100, 100) : 0;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        순공부시간
      </Typography>

      {isLoading ? (
        <Typography>로딩 중...</Typography>
      ) : (
        <>
          {/* 오늘 목표 카드 */}
          <Card sx={{ mt: 2, mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccessTimeIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  오늘의 공부시간
                </Typography>
              </Box>

              <Typography variant="h3" fontWeight={700} color="primary" gutterBottom>
                {formatTime(data?.today_minutes || 0)}
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    목표: 4시간
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {todayProgress.toFixed(0)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={todayProgress}
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* 통계 카드들 */}
          <Grid container spacing={2}>
            <Grid size={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TrendingUpIcon color="success" />
                    <Typography variant="body2" color="text.secondary">
                      이번 주
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={600}>
                    {formatTime(data?.week_minutes || 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <EmojiEventsIcon sx={{ color: '#ffc107' }} />
                    <Typography variant="body2" color="text.secondary">
                      순위
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={600}>
                    {data?.rank ? `${data.rank}위` : '-'}
                    {data?.total_students && (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {' '}/ {data.total_students}명
                      </Typography>
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* 이번 달 통계 */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                이번 달 누적
              </Typography>
              <Typography variant="h4" fontWeight={700} color="primary">
                {formatTime(data?.month_minutes || 0)}
              </Typography>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
